import { Injectable, inject, isDevMode } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { tapResponse } from '@ngrx/component-store';
import { EventBusEvents } from '@siza-staff-care/event-bus-constants';
import { AppStateService } from '@siza-staff-care/shared/app-state';
import {
	LookupType,
	SelectOption,
	StaffSecurityEditDto,
	StaffSecurityEditItemDto,
	Types,
	obfuscate,
	tryDecode,
} from '@siza-staff-care/shared/data';
import { GenericEditStore } from '@siza-staff-care/shared/generic-edit';
import {
	EditFlagsValue,
	EditFlags_All,
	EditFlags_Create,
	EditFlags_Delete,
	EditFlags_Update,
	MenuBuilderService,
	securityPaths,
	securityPathsReverseLookup,
} from '@siza-staff-care/shared/global-services';
import { IStore } from '@siza-staff-care/shared/ui-components/store-registry';
import { TreeItem, assertDefined, buildTree, typedClone } from '@siza-staff-care/shared/utils';
import { GridApi } from 'ag-grid-community';
import { flatMap, isEqual, keyBy } from 'lodash-es';
import {
	EMPTY,
	Observable,
	debounceTime,
	delay,
	exhaustMap,
	finalize,
	from,
	map,
	of,
	switchMap,
	take,
	tap,
	withLatestFrom,
} from 'rxjs';

const DEBUG = true;

export const enum SecurityForTypes {
	Groups = 'Groups',
	Staff = 'Staff',
}

export const Type_Control = 'control';
export const Type_Editor = 'editor';
export const Type_Form = 'form';
export const Type_TopMenu = 'menu';

export const Access_Full = 'F';
export const Access_None = 'N';
export const Access_ReadOnly = 'R';
export const Access_Group = 'G';
export const Access_Mandatory = 'M';

export const iconMap = {
	[Access_Full]: 'done',
	[Access_ReadOnly]: 'visibility',
	[Access_None]: 'block',
	[Access_Group]: 'group',
	[Access_Mandatory]: 'star',
};

export const typeIconMap = {
	[EditFlags_Create]: 'add',
	[EditFlags_Update]: 'edit',
	[EditFlags_Delete]: 'delete',
};

export const enum AccessLevels {
	Full = Access_Full,
	ReadOnly = Access_ReadOnly,
	None = Access_None,
	Group = Access_Group,
	Mandatory = Access_Mandatory,
}

export const SecurityLevel_None = 0;
export const SecurityLevel_Disabled = 1;
export const SecurityLevel_Hidden = 2;
export const SecurityLevel_Group = 3;
export const securityLevel_Mandatory = 3;

/*
	Regex for controls in the form of top.screen.control, or top.screen.control-prefix.control
	Captures groups:
		1: top.screen
		last: control or control-prefix.control, etc.
*/
const controlRegex = /^([^.]*\.[^.]*)\.(.*)/;

type SecurityForType = SecurityForTypes.Groups | SecurityForTypes.Staff;

type UpdateAccessParams = {
	key: string;
	editItem?: StaffSecurityEditItemDto;
	level: AccessLevels;
	controlName?: string;
	modelProp?: string;
	dtoName?: string;
};

interface SetMenuKeySelectionParams {
	menuKey: string;
	selected: boolean;
}

interface SetEditorFlagsToGroupParams {
	menuKey: string;
	storeKey: string;
}

interface ToggleEditorFlagParams {
	menuKey: string;
	storeKey: string | undefined;
	editorFlag: EditFlagsValue;
}

export interface SelectorContext {
	mySecurityGroup?: string;
	myStaffMaster?: string;
	securityFor: SecurityForType;
	updateSubgroups?: boolean;
}

type PaintResumeData = {
	selectorContext: SelectorContext;
	selectAll: boolean;
	selectedMenuHierarchyKeys: Set<string>;
	priorEdit?: StaffSecurityEditDto;
	edit: StaffSecurityEditDto;
	hasUnsavedChanges: boolean;
};

export type FormItemsBySecurityKey = Record<string, StaffSecurityEditItemDto>;
export type ControlItemsBySecurityKey = Record<string, StaffSecurityEditItemDto[]>;
export type EditorFlagsItemsBySecurityKey = Record<string, StaffSecurityEditItemDto[]>;

interface SecurityConfigurationState {
	selectorContext?: SelectorContext;
	lastSelectorContext?: SelectorContext;

	selectorForm?: FormGroup;

	gridApi?: GridApi;
	selectAll?: boolean;

	formItemsBySecurityKey?: FormItemsBySecurityKey;
	controlItemsBySecurityKey?: ControlItemsBySecurityKey;
	editorFlagsItemsBySecurityKey?: EditorFlagsItemsBySecurityKey;

	loaded?: boolean;

	selectedMenuHierarchyKeys: Set<string>;

	paintResumeData?: PaintResumeData;

	workflowStore?: IStore;
}

const lookupTypes: LookupType[] = ['StaffSecurityGroup'];

@Injectable()
export class SecurityConfigurationStore extends GenericEditStore<
	StaffSecurityEditDto,
	StaffSecurityEditDto,
	SecurityConfigurationState
> {
	url = 'security-config';
	niceName = 'Security configuration';
	controllerType = Types.StaffSecurityEditorController;

	private route = inject(ActivatedRoute);
	private appState = inject(AppStateService);
	private menuBuilder = inject(MenuBuilderService);

	readonly staffSecurityGroups$ = this.appState.getLookupCollection('StaffSecurityGroup');
	readonly groupsAsTree$ = this.select(this.staffSecurityGroups$, (staffSecurityGroups) =>
		staffSecurityGroups ? buildTree(staffSecurityGroups) : ([] as TreeItem[]),
	).pipe(
		tap((tree) => {
			// Build a lookup of items by their value (which is the OID) which can be used to set the indent.
			this.itemsByOid = (tree || []).reduce(
				(acc, item) => {
					acc[item.value] = item;
					return acc;
				},
				{} as Record<string, TreeItem>,
			);
		}),
		map((tree) => tree.map((treeNode) => treeNode as SelectOption)),
	);
	readonly otherGroups$ = this.select(this.staffSecurityGroups$, this.edit$, (staffSecurityGroups, edit) =>
		staffSecurityGroups.filter((g) => g.oid !== edit?.oid),
	);
	readonly workflowStore$ = this.select(this.additionalState$, (additionalState) => additionalState?.workflowStore);

	override lookupTypes = lookupTypes;
	itemsByOid?: Record<string, TreeItem>;

	constructor() {
		super();

		this.setAllowedEditButtons({
			allowNew: false,
			allowDelete: false,
		});

		this.patchAdditional({
			selectedMenuHierarchyKeys: new Set<string>(),
		});

		this.watchCancelSelection();
		this.watchForSessionStartOrEnd(this._eventBus.on(EventBusEvents.Event_Session_Start_End));
	}

	readonly selectorContext$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.selectorContext || ({} as SelectorContext),
	);

	private readonly lastSelectorContext$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.lastSelectorContext,
	);

	readonly selectorForm$ = this.select(this.additionalState$, (additionalState) => additionalState?.selectorForm);
	readonly gridApi$ = this.select(this.additionalState$, (additionalState) => additionalState?.gridApi);
	readonly staffMasterGroupName$ = this.select(this.edit$, (edit) => edit?.groupName || '');
	readonly forGroups$ = this.select(
		this.selectorContext$,
		(selectorContext) => selectorContext?.securityFor === SecurityForTypes.Groups,
	);
	readonly forStaff$ = this.select(
		this.selectorContext$,
		(selectorContext) => selectorContext?.securityFor === SecurityForTypes.Staff,
	);
	readonly hasSelectedStaff$ = this.select(
		this.forStaff$,
		this.realOid$,
		(forStaff, realOid) => !!(forStaff && realOid),
	);

	readonly editItems$ = this.select(this.edit$, (edit) => edit?.allEditItems || []);

	readonly formItemsBySecurityKey$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.formItemsBySecurityKey || {},
	);
	readonly controlItemsBySecurityKey$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.controlItemsBySecurityKey || {},
	);
	readonly editorFlagsItemsBySecurityKey$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.editorFlagsItemsBySecurityKey || {},
	);

	readonly selectedMenuHierarchyKeys$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.selectedMenuHierarchyKeys || new Set<string>(),
	);

	readonly selectAll$ = this.select(this.additionalState$, (additionalState) => !!additionalState?.selectAll);
	readonly hasAnySelections$ = this.select(
		this.selectAll$,
		this.selectedMenuHierarchyKeys$,
		(selectAll, selectedMenuHierarchyKeys) => selectAll || !!selectedMenuHierarchyKeys.size,
	);

	readonly loaded$ = this.select(this.additionalState$, (additionalState) => !!additionalState?.loaded);

	private readonly paintResumeData$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.paintResumeData,
	);

	readonly hasAnyUnsavedChanges$ = this.select(
		this.hasUnsavedChanges$,
		this.paintResumeData$,
		(hasUnsavedChanges, paintResumeData) => !!(hasUnsavedChanges || paintResumeData?.hasUnsavedChanges),
	);

	readonly vm$ = this.select({
		selectorContext: this.selectorContext$,
		menuHierarchy: this.menuBuilder.menuHierarchy$,
		editItems: this.editItems$,
		forStaff: this.forStaff$,
		forGroups: this.forGroups$,
		loaded: this.loaded$,
		selectAll: this.selectAll$,
		hasAnySelections: this.hasAnySelections$,
	});

	get gridApi() {
		const gridApi = this.get().additionalState?.gridApi;
		assertDefined(gridApi);

		return gridApi;
	}

	readonly selectById = this.effect((id$: Observable<string>) =>
		id$.pipe(
			withLatestFrom(this.selectorContext$),
			tap(([id, selectorContext]) => {
				assertDefined(selectorContext);

				if (selectorContext.securityFor === SecurityForTypes.Groups) {
					selectorContext.mySecurityGroup = id;
				} else {
					selectorContext.myStaffMaster = id;
				}

				this.patchState({});

				this.patchAdditional({
					selectorContext: {
						...selectorContext,
					},
				});
			}),
		),
	);

	readonly updateSelectorContext = this.effect((context$: Observable<SelectorContext>) =>
		context$.pipe(
			withLatestFrom(this.lastSelectorContext$),
			switchMap(([context, lastContext]) => {
				if (context.securityFor === lastContext?.securityFor) {
					return of(context);
				}

				// If the type has changed, we need to clear. Check for unsaved changes first.
				return this.checkUnsavedChangesAndClear().pipe(
					delay(0), // Wait for navigation to complete
					switchMap((ok) => {
						if (!ok) {
							return EMPTY;
						}

						return of(context);
					}),
				);
			}),
			withLatestFrom(this.lastSelectorContext$),
			tap(([context, oldContext]) => {
				if (context && oldContext) {
					if (isEqual(context, oldContext)) {
						return;
					}
				}

				const copiedContext = {
					...context,
				};

				// Blank out any other selection.
				if (copiedContext.securityFor === SecurityForTypes.Groups) {
					copiedContext.myStaffMaster = undefined;
				} else if (copiedContext.securityFor === SecurityForTypes.Staff) {
					copiedContext.mySecurityGroup = undefined;
				}

				this.patchAdditional({ selectorContext: copiedContext, lastSelectorContext: context });

				this._router
					.navigate([], {
						relativeTo: this.activatedRoute,
						queryParams: {
							securityFor: context.securityFor,
							updateSubgroups: context.updateSubgroups,
						},
						queryParamsHandling: 'merge',
					})
					.then(() => {
						const selectionOid =
							context.securityFor === SecurityForTypes.Groups
								? context.mySecurityGroup
								: context.myStaffMaster;

						const lastSelectionOid =
							oldContext?.securityFor === SecurityForTypes.Groups
								? oldContext?.mySecurityGroup
								: oldContext?.myStaffMaster;

						// Don't navigate if the selection hasn't changed.
						if (selectionOid === lastSelectionOid) {
							return;
						}

						if (selectionOid) {
							this.routeToEditReal(selectionOid);
						} else {
							this.clear();
						}
					});
			}),
		),
	);

	readonly clearSelection = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.selectorContext$),
			tap(([, selectorContext]) => {
				assertDefined(selectorContext);

				selectorContext.mySecurityGroup = undefined;
				selectorContext.myStaffMaster = undefined;

				this.patchAdditional({
					selectorContext: {
						...selectorContext,
					},
					lastSelectorContext: selectorContext,
				});
			}),
		),
	);

	private readonly watchCancelSelection = this.effect((trigger$) =>
		trigger$.pipe(
			switchMap(() => {
				return this.cancelSelection$.pipe(
					debounceTime(100),
					withLatestFrom(this.selectorContext$),
					tap(([lastSelection, selectorContext]) => {
						if (lastSelection) {
							lastSelection = tryDecode(lastSelection) ?? undefined;
						}

						this.patchAdditional({
							lastSelectorContext: { ...selectorContext, mySecurityGroup: '' },
							selectorContext: { ...selectorContext, mySecurityGroup: lastSelection },
						});
					}),
				);
			}),
		),
	);

	readonly watchForSessionStartOrEnd = this.effect((event$: Observable<unknown>) =>
		event$.pipe(
			tap(() => {
				this.abandonPaintState();
			}),
		),
	);

	readonly registerSelectorForm = this.effect((form$: Observable<FormGroup>) =>
		form$.pipe(
			tap((form) => {
				this.patchAdditional({
					selectorForm: form,
				});
			}),
		),
	);

	readonly buildSelectorContextFromQueryStringParams = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				const queryParamMap = this.route.snapshot.queryParamMap;

				let securityFor = queryParamMap.get('securityFor') ?? '';
				if (['Groups', 'Staff'].indexOf(securityFor) === -1) {
					securityFor = 'Groups';
				}

				const updateSubgroups = queryParamMap.get('updateSubgroups') === 'true';

				const selector: SelectorContext = {
					securityFor: securityFor as SecurityForType,
					updateSubgroups,
				};

				this.patchAdditional({
					selectorContext: selector,
					lastSelectorContext: { ...selector },
				});
			}),
		),
	);

	readonly setSelectAll = this.effect((selectAll$: Observable<boolean>) =>
		selectAll$.pipe(
			tap((selectAll) => {
				this.patchAdditional({
					selectAll,
					selectedMenuHierarchyKeys: new Set<string>(),
				});
			}),
		),
	);

	readonly setMenuKeySelection = this.effect((params$: Observable<SetMenuKeySelectionParams>) =>
		params$.pipe(
			withLatestFrom(this.selectedMenuHierarchyKeys$),
			tap(([params, selections]) => {
				params.selected ? selections.add(params.menuKey) : selections.delete(params.menuKey);

				this.patchAdditional({
					selectedMenuHierarchyKeys: new Set<string>(selections),
				});
			}),
		),
	);

	readonly clearItems = this.effect((menuKey$: Observable<string>) =>
		menuKey$.pipe(
			withLatestFrom(this.controlItemsBySecurityKey$, this.editorFlagsItemsBySecurityKey$),
			tap(([menuKey, controlItems, editorFlagsItems]) => {
				controlItems[menuKey] = [];
				editorFlagsItems[menuKey] = [];

				this.patchAdditional({
					controlItemsBySecurityKey: typedClone(controlItems),
					editorFlagsItemsBySecurityKey: typedClone(editorFlagsItems),
				});

				this.markDirty();
			}),
		),
	);

	readonly setAllToLevel = this.effect((level$: Observable<AccessLevels>) =>
		level$.pipe(
			withLatestFrom(this.selectAll$, this.selectedMenuHierarchyKeys$, this.formItemsBySecurityKey$),
			tap(([level, selectAll, selections, formItemsBySecurityKey]) => {
				if (selectAll) {
					// Set all the form items to the level.
					Object.values(formItemsBySecurityKey).forEach((item) => {
						item.level = level;
					});
				} else {
					// Set all the selected items to the level.
					selections.forEach((key) => {
						const item = formItemsBySecurityKey[key];
						if (item) {
							item.level = level;
						}
					});
				}

				this.patchAdditional({
					formItemsBySecurityKey: typedClone(formItemsBySecurityKey),
				});

				this.markDirty();
			}),
		),
	);

	readonly copyFromGroup = this.effect((otherGroup$: Observable<string>) =>
		otherGroup$.pipe(
			exhaustMap((otherGroup) => {
				this.setLoadingEdit(true);

				return this._crud.select(this.controllerType, otherGroup).pipe(
					tapResponse(
						(result) => {
							this.handleEditSet(result.dtos[0]);
							this.setHasUnsavedChanges(true);
						},
						(err) => this.handleGenericError(err),
					),
					finalize(() => this.setLoadingEdit(false)),
				);
			}),
		),
	);

	readonly updateAccess = this.effect((params$: Observable<UpdateAccessParams>) =>
		params$.pipe(
			withLatestFrom(this.formItemsBySecurityKey$, this.controlItemsBySecurityKey$),
			switchMap(([params, formItemsBySecurityKey, controlItemsBySecurityKey]) => {
				params.dtoName = params.dtoName?.replace('EditDto', '');

				if (isDevMode()) {
					console.info('updateAccess', params);
				}

				if (params.controlName) {
					return this.getControlItem(params.key, params.controlName).pipe(
						take(1),
						tap((controlItem) => {
							assertDefined(params.controlName);
							const securityPath = this.buildSecurityPathForControl(params.key, params.controlName);

							controlItem = {
								...controlItem,
								securityPath,
								level: params.level,
								modelProp: params.modelProp ?? '',
								dtoName: params.dtoName ?? '',
							};

							let controlItems = controlItemsBySecurityKey[params.key] || [];
							controlItems = controlItems
								.filter((item) => item.securityPath !== securityPath)
								.concat(controlItem);

							controlItemsBySecurityKey[params.key] = controlItems;

							this.patchAdditional({
								controlItemsBySecurityKey: {
									...controlItemsBySecurityKey,
								},
							});

							this.markDirtyForPaintState();
						}),
					);
				} else {
					return this.getFormItem(params.key).pipe(
						take(1),
						tap((formItem) => {
							formItem = {
								...formItem,
								level: params.level,
							};

							formItemsBySecurityKey[params.key] = formItem;

							this.patchAdditional({
								formItemsBySecurityKey: {
									...formItemsBySecurityKey,
								},
							});

							this.markDirtyForPaintState();
						}),
					);
				}
			}),
		),
	);

	readonly toggleEditorFlag = this.effect((params$: Observable<ToggleEditorFlagParams>) =>
		params$.pipe(
			switchMap((params) => {
				return this.getEditorFlagsItems(params.menuKey).pipe(
					take(1),
					withLatestFrom(this.editorFlagsItemsBySecurityKey$, this.forStaff$),
					tap(([items, allEditorFlagsItems, forStaff]) => {
						let item = items[params.storeKey || ''];
						if (!item) {
							item = {
								$type: Types.StaffSecurityEditItemDto,
								securityPath: securityPaths[params.menuKey] + '.' + params.storeKey,
								editorFlags: EditFlags_All,
								defaultEditorFlags: EditFlags_All,
								type: Type_Editor,
							} as StaffSecurityEditItemDto;

							items[params.storeKey || ''] = item;

							if (!allEditorFlagsItems[params.menuKey]) {
								allEditorFlagsItems[params.menuKey] = [];
							}
							allEditorFlagsItems[params.menuKey].push(item);

							DEBUG && console.log('Created new item', item);
						}

						// If level is set to Group, use the default flags, otherwise the actual editorFlags on the item.
						const editorFlags = item.level === Access_Group ? item.defaultEditorFlags : item.editorFlags;

						const currentFlag = (editorFlags & params.editorFlag) > 0;
						if (currentFlag) {
							item.editorFlags &= ~params.editorFlag;
						} else {
							item.editorFlags |= params.editorFlag;
						}

						if (forStaff) {
							// Clear the Group setting as the item has been edited.
							item.level = '';
						}

						DEBUG && console.log('toggleEditorFlag', item);

						this.patchAdditional({
							editorFlagsItemsBySecurityKey: typedClone(allEditorFlagsItems),
						});

						this.markDirtyForPaintState();
					}),
				);
			}),
		),
	);

	/**
	 * When editing security for an individual staff member, sets the EditorFlags items back to the default for the group.
	 */
	readonly setEditorFlagsForStaffToGroup = this.effect((params$: Observable<SetEditorFlagsToGroupParams>) =>
		params$.pipe(
			withLatestFrom(this.editorFlagsItemsBySecurityKey$),
			switchMap(([params, editorFlagsItems]) => {
				return this.getEditorFlagsItems(params.menuKey).pipe(
					take(1),
					tap((items) => {
						const item = items[params.storeKey];
						item.level = Access_Group;

						this.patchAdditional({
							editorFlagsItemsBySecurityKey: typedClone(editorFlagsItems),
						});

						this.markDirtyForPaintState();
					}),
				);
			}),
		),
	);

	/**
	 * Depending on whether we're currently painting security, sets the hasUnsavedChanges flag either on the PaintResumeData or on the store.
	 */
	private readonly markDirtyForPaintState = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(
				this.appState.isPaintingSecurity$,
				this.appState.isPaintingWorkflowSecurity$,
				this.paintResumeData$,
				this.workflowStore$,
			),
			tap(([, isPaintingSecurity, isPaintingWorkflowSecurity, paintResumeData, workflowStore]) => {
				if (isPaintingSecurity) {
					assertDefined(paintResumeData);
					paintResumeData.hasUnsavedChanges = true;
					//					this.appState.setSecurityConfigurationHasUnsavedChanges(true);
				} else if (isPaintingWorkflowSecurity) {
					assertDefined(workflowStore);
					workflowStore.setHasUnsavedChanges(true);
					//					this.appState.setSecurityConfigurationHasUnsavedChanges(true);
				} else {
					this.markDirty();
				}
			}),
		),
	);

	readonly setWorkflowStore = this.effect((workflowStore$: Observable<IStore | undefined>) =>
		workflowStore$.pipe(
			tap((workflowStore) => {
				this.patchAdditional({
					workflowStore,
				});
			}),
		),
	);

	readonly startPaintAndRoute = this.effect((route$: Observable<string>) =>
		route$.pipe(
			tap((route) => {
				this.beginPaintOnRoute(route);
			}),
		),
	);

	readonly beginPaint = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.beginPaintOnRoute('/');
			}),
		),
	);

	/**
	 * Attempts to end paint (will not end if routing fails - e.g. if there are unsaved changes and the user cancels)
	 */
	readonly endPaint = this.effect((trigger$) =>
		trigger$.pipe(
			switchMap(() => {
				return from(this._router.navigate(['security-config']));
			}),
			delay(0),
			tap((routedOk) => {
				if (!routedOk) {
					return;
				}

				this.appState.clearPaintingSecurity();
			}),
		),
	);

	readonly abandonPaintState = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchAdditional({
					paintResumeData: undefined,
				});

				this.appState.clearPaintingSecurity();
			}),
		),
	);

	readonly openIfNotResumingFromEndPaint = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.paintResumeData$),
			tap(([, paintResumeData]) => {
				this.appState.setScreenName('Security configuration');

				if (paintResumeData) {
					this.handleResumeFromPaint(paintResumeData);
				} else {
					this.buildSelectorContextFromQueryStringParams();
					this.open();
				}
			}),
		),
	);

	private readonly handleResumeFromPaint = this.effect((data$: Observable<PaintResumeData>) =>
		data$.pipe(
			switchMap((paintResumeData) => {
				// Grab the OID to select
				const editOid = obfuscate(paintResumeData.edit.oid);

				// Don't go to the server - use the saved edit instead.
				this.setInsteadOfSelect(paintResumeData.edit);

				// Route to the selected group/staff member.
				return from(this._router.navigate([editOid], { relativeTo: this.activatedRoute })).pipe(
					switchMap((navigateOk) => {
						if (!navigateOk) {
							// Something went wrong - abandon the paint state.
							this.patchAdditional({
								paintResumeData: undefined,
							});
							return EMPTY;
						}

						// Restore the selector context.
						this.patchAdditional({
							selectorContext: paintResumeData.selectorContext,
						});
						return of(paintResumeData);
					}),
					delay(0),
					tap((paintResumeData) => {
						// Set the prior edit
						if (paintResumeData.priorEdit) {
							this.patchState({
								priorEdit: paintResumeData.priorEdit,
							});
						}

						// Restore the rest of the data.
						this.patchAdditional({
							paintResumeData: undefined,
							selectAll: paintResumeData.selectAll,
							selectedMenuHierarchyKeys: new Set<string>(paintResumeData.selectedMenuHierarchyKeys || []),
						});
					}),
					delay(0),
					tap((paintResumeData) => {
						// Restore the dirty state.
						if (paintResumeData.hasUnsavedChanges) {
							this.markDirty();
							this.setHasUnsavedChanges(true);
						}
					}),
				);
			}),
		),
	);

	private readonly beginPaintOnRoute = this.effect((route$: Observable<string>) =>
		route$.pipe(
			withLatestFrom(this.additionalState$, this.edit$, this.priorEdit$, this.hasUnsavedChanges$, this.forStaff$),
			tap(([route, additionalState, edit, priorEdit, hasUnsavedChanges, forStaff]) => {
				assertDefined(additionalState?.selectorContext);
				assertDefined(edit);

				// Build the data to resume whe paint state ends.
				const paintResumeData: PaintResumeData = {
					selectorContext: additionalState?.selectorContext,
					selectAll: !!additionalState?.selectAll,
					selectedMenuHierarchyKeys: new Set<string>(additionalState?.selectedMenuHierarchyKeys || []),
					edit,
					priorEdit,
					hasUnsavedChanges,
				};

				this.patchAdditional({
					paintResumeData,
				});

				// Ensure we don't have changes.
				this.setHasUnsavedChanges(false);

				// Start paint mode, and route to the correct state.
				this.appState.setIsPaintingSecurity({ isPaintingSecurity: true, isPaintingSecurityForStaff: forStaff });
				this._router.navigate([route]);
			}),
		),
	);

	isMenuKeySelected(menuKey: string) {
		return this.selectedMenuHierarchyKeys$.pipe(
			withLatestFrom(this.selectAll$),
			map(([keys, selectAll]) => {
				return selectAll || keys.has(menuKey);
			}),
		);
	}

	getFormItem(menuKey: string): Observable<StaffSecurityEditItemDto> {
		return this.formItemsBySecurityKey$.pipe(
			map((formItemsBySecurityKey) => {
				const key = menuKey;
				const item = formItemsBySecurityKey[key];
				return (
					item ||
					({
						$type: Types.StaffSecurityEditItemDto,
						type: Type_Form,
						level: Access_Full,
						name: '(constructed)',
						securityPath: menuKey,
					} as StaffSecurityEditItemDto)
				);
			}),
		);
	}

	/**
	 * Returns an Observable of the edit item for the control, return an empty item with full access if not found.
	 * @param menuKey The menu key - e.g. StaffMaintenance
	 * @param controlName The control name (with any prefix) e.g. myMarital or contract.startDate
	 * @returns
	 */
	getControlItem(menuKey: string, controlName: string): Observable<StaffSecurityEditItemDto> {
		return this.controlItemsBySecurityKey$.pipe(
			map((controlItemsBySecurityKey) => {
				// Note - for SCA-184, we shouldn't be doing this.

				// Try to translate the key - e.g. My Record (StaffMaintenance - end user) to StaffRecords

				const key = this.menuBuilder.getTranslatedKey(menuKey);
				const items = controlItemsBySecurityKey[key] || [];

				const securityPath = this.buildSecurityPathForControl(key, controlName);

				const controlItem = items.find((item) => item.securityPath === securityPath);
				return (
					controlItem ||
					({
						$type: Types.StaffSecurityEditItemDto,
						type: Type_Control,
						level: Access_Full,
					} as StaffSecurityEditItemDto)
				);
			}),
		);
	}

	getControlItems(menuKey: string): Observable<StaffSecurityEditItemDto[]> {
		return this.controlItemsBySecurityKey$.pipe(
			map((controlItemsBySecurityKey) => {
				const key = this.menuBuilder.getTranslatedKey(menuKey);
				return controlItemsBySecurityKey[key] || [];
			}),
		);
	}

	/**
	 * Gets the editor flags edit items for a security key. Returns a hash of the items keyed by the store key.
	 * @param menuKey
	 * @returns
	 */
	getEditorFlagsItems(menuKey: string): Observable<Record<string, StaffSecurityEditItemDto>> {
		return this.editorFlagsItemsBySecurityKey$.pipe(
			map((editorFlagsItemsBySecurityKey) => {
				const key = this.menuBuilder.getTranslatedKey(menuKey);
				const items = editorFlagsItemsBySecurityKey[key] || [];
				const keyed = keyBy(items, (i) => {
					// Use a regex to return only the end of the security path - e.g. StaffContract
					const groups = controlRegex.exec(i.securityPath);
					assertDefined(groups);
					return groups[groups.length - 1];
				});

				return keyed;
			}),
		);
	}

	private buildSecurityPathForControl(menuKey: string, controlName: string) {
		const key = this.menuBuilder.getTranslatedKey(menuKey);
		const securityPath = securityPaths[key] + '.' + controlName;
		return securityPath;
	}

	private readonly buildFormItemsBySecurityKey = this.effect(
		(edit$: Observable<{ allEditItems: StaffSecurityEditItemDto[] }>) =>
			edit$.pipe(
				tap((edit) => {
					const entries = edit.allEditItems;

					// Find all the items for forms (and their children, which will be control items)
					const findFormItems = (children: StaffSecurityEditItemDto[], items: StaffSecurityEditItemDto[]) => {
						children.forEach((item) => {
							if (item.type === Type_Form || item.type === Type_TopMenu) {
								// Ignore label items.
								if (item.securityPath.startsWith(':label')) {
									return;
								}
								items.push(item);
							}

							findFormItems(item.allChildItems, items);
						});
					};

					const formItems = [] as StaffSecurityEditItemDto[];
					findFormItems(entries, formItems);

					const formItemsBySecurityKey = formItems.reduce((acc, item) => {
						acc[item.securityPath] = item;
						return acc;
					}, {} as FormItemsBySecurityKey);

					this.patchAdditional({
						formItemsBySecurityKey,
					});
				}),
			),
	);

	private readonly buildControlItemsBySecurityKey = this.effect(
		(edit$: Observable<{ allEditItems: StaffSecurityEditItemDto[] }>) =>
			edit$.pipe(
				tap((edit) => {
					const entries = edit.allEditItems;

					const controlItems = entries.filter((item) => item.type === Type_Control);

					/*
						Build a lookup of the control items using the security key for the screen. This is derived
						from the first two parts of the security path to identify the associated screen.

						E.g. staff.staffMaintenance.myEthnicity will be under staff.staffMaintenance
						This then translates to StaffMaintenance, which is used as the security key.
					*/
					const controlItemsBySecurityKey = controlItems.reduce((acc, item) => {
						const groups = controlRegex.exec(item.securityPath);
						assertDefined(groups);

						const securityPath = groups[1];
						const securityKey = securityPathsReverseLookup[securityPath];

						if (!acc[securityKey]) {
							acc[securityKey] = [];
						}

						acc[securityKey].push(item);
						return acc;
					}, {} as ControlItemsBySecurityKey);

					this.patchAdditional({
						controlItemsBySecurityKey,
					});
				}),
			),
	);

	private readonly buildEditorFlagsItemsBySecurityKey = this.effect(
		(edit$: Observable<{ allEditItems: StaffSecurityEditItemDto[] }>) =>
			edit$.pipe(
				tap((edit) => {
					const entries = edit.allEditItems;

					const editorFlagsItems = entries.filter((item) => item.type === Type_Editor);

					/*
					Build a lookup of the editor flags items using the security key for the screen. This is derived
					from the first two parts of the security path to identify the associated screen.

					E.g. staff.staffMaintenance.StaffContract will be under staff.staffMaintenance
					This then translates to StaffMaintenance, which is used as the security key for the 
					StaffContract editorFlags item.
				*/
					const editorFlagsItemsBySecurityKey = editorFlagsItems.reduce((acc, item) => {
						const groups = controlRegex.exec(item.securityPath);
						assertDefined(groups);

						const securityPath = groups[1];
						const securityKey = securityPathsReverseLookup[securityPath];

						if (!acc[securityKey]) {
							acc[securityKey] = [];
						}

						acc[securityKey].push(item);
						return acc;
					}, {} as EditorFlagsItemsBySecurityKey);

					this.patchAdditional({
						editorFlagsItemsBySecurityKey,
					});
				}),
			),
	);

	readonly handleEditSet = this.effect((edit$: Observable<{ allEditItems: StaffSecurityEditItemDto[] } | null>) =>
		edit$.pipe(
			withLatestFrom(this.appState.isPaintingSecurity$),
			tap(([edit, isPaintingSecurity]) => {
				// If painting security, don't clear anything as we need these lookups to be available.
				if (isPaintingSecurity) {
					return;
				}

				if (!edit) {
					// Clear the lookups for form items, so that the grid won't re-render until they're rebuilt (below).
					this.patchAdditional({
						formItemsBySecurityKey: {},
						controlItemsBySecurityKey: {},
						editorFlagsItemsBySecurityKey: {},
					});
					return;
				}

				this.buildFormItemsBySecurityKey(edit);
				this.buildControlItemsBySecurityKey(edit);
				this.buildEditorFlagsItemsBySecurityKey(edit);

				this.patchAdditional({
					loaded: true,
					selectAll: false,
					selectedMenuHierarchyKeys: new Set<string>(),
				});
			}),
		),
	);

	protected override afterOpen(): void {
		this.buildSelectorContextFromQueryStringParams();
	}

	protected override onEditSet(edit: StaffSecurityEditDto | null): void {
		this.handleEditSet(edit);
	}

	protected override onRestore(edit: StaffSecurityEditDto) {
		this.onEditSet(edit);
	}

	protected override preSave(edit: StaffSecurityEditDto): Observable<StaffSecurityEditDto | null> {
		return this.selectorContext$.pipe(
			take(1),
			switchMap((selectorContext) => {
				if (selectorContext.updateSubgroups) {
					edit.updateSubGroups = true;

					return this._dialogs
						.confirm({
							title: 'Update subgroups?',
							message:
								'Are you sure you want to update the subgroups? This will make all subgroups have the same security settings as this group.',
						})
						.pipe(
							switchMap((ok) => {
								if (!ok) {
									return EMPTY;
								}

								return of(edit);
							}),
						);
				}

				return of(edit);
			}),
			withLatestFrom(
				this.formItemsBySecurityKey$,
				this.controlItemsBySecurityKey$,
				this.editorFlagsItemsBySecurityKey$,
			),
			switchMap(([edit, formItems, controlItems, editorFlagsItems]) => {
				const formItemValues = Object.values(formItems);
				const controlItemValues = flatMap(Object.values(controlItems));
				const editorFlagsItemValues = flatMap(Object.values(editorFlagsItems));

				edit.allEditItems = [...formItemValues, ...controlItemValues, ...editorFlagsItemValues];
				edit.allEditItems.forEach((i) => (i.allChildItems = []));

				return of(edit);
			}),
		);
	}
}
